import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { NEUTRAL, ORANGE, RADIUS, VIOLET } from "../../styles/brandTokens";
import { text } from "../monitor/SummaryPanel";

const headCellSx = {
  ...text(11.5, NEUTRAL.textSecondary, 700),
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  borderBottom: `1px solid ${NEUTRAL.border}`,
  background: NEUTRAL.surface,
  py: 1,
  whiteSpace: "nowrap" as const,
};
const cellSx = { ...text(13.5), borderBottom: `1px solid ${NEUTRAL.border}`, verticalAlign: "top", py: 1.25 };
const numberSx = { ...cellSx, textAlign: "right" as const, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" as const };

export interface SourceColumn<Row> {
  key: string;
  header: string;
  numeric?: boolean;
  render: (row: Row) => React.ReactNode;
  /** Shown in the totals row, when the dialog has one. */
  total?: React.ReactNode;
}

export interface SourceState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches a figure's records only while its dialog is open, and again when the filters change.
 * Only the latest request may land, so a slow answer for old filters never replaces a newer one.
 */
export function useSourceList<T>(open: boolean, requestKey: string, load: () => Promise<T>): SourceState<T> {
  const [state, setState] = useState<SourceState<T>>({ data: null, loading: false, error: null });
  const requestRef = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!open) return;
    const requestId = ++requestRef.current;
    setState({ data: null, loading: true, error: null });
    loadRef
      .current()
      .then((data) => {
        if (requestId === requestRef.current) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (requestId !== requestRef.current) return;
        setState({ data: null, loading: false, error: err instanceof Error ? err.message : "Couldn't load this list." });
      });
  }, [open, requestKey]);

  return state;
}

interface SourceListDialogProps<Row> {
  open: boolean;
  onClose: () => void;
  /** Prefix for element ids; the title is the dialog's accessible name. */
  id: string;
  title: string;
  scopeLabel: string;
  state: SourceState<{ rows: Row[] }>;
  columns: SourceColumn<Row>[];
  rowKey: (row: Row, index: number) => string;
  emptyText: string;
  /** After the scope line once loaded, e.g. "612 runs". */
  summary?: React.ReactNode;
  /** Below the table: what is counted and what is left out. */
  footnote?: React.ReactNode;
  showTotals?: boolean;
  minTableWidth?: number;
}

function SourceListDialog<Row>({
  open,
  onClose,
  id,
  title,
  scopeLabel,
  state,
  columns,
  rowKey,
  emptyText,
  summary,
  footnote,
  showTotals = false,
  minTableWidth = 640,
}: SourceListDialogProps<Row>) {
  const rows = state.data?.rows || [];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby={`${id}-title`}
      PaperProps={{ sx: { borderRadius: `${RADIUS.well}px` } }}
    >
      <DialogTitle id={`${id}-title`} sx={{ ...text(20, NEUTRAL.text, 700), pr: 6, pb: 0.5 }}>
        {title}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12, color: NEUTRAL.textSecondary, borderRadius: `${RADIUS.control}px` }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ ...text(13.5, NEUTRAL.textSecondary), mb: 2 }}>
          {scopeLabel}
          {summary && state.data ? <> · {summary}</> : null}
        </Typography>
        {state.loading ? (
          <CircularProgress size={22} sx={{ color: VIOLET.main }} aria-label={`Loading ${title}`} />
        ) : state.error ? (
          <Typography role="alert" sx={text(13.5, ORANGE.text)}>
            {state.error}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography sx={text(13.5, NEUTRAL.textMuted)}>{emptyText}</Typography>
        ) : (
          <Box sx={{ overflow: "auto", mx: -1, maxHeight: "60vh" }}>
            <Table size="small" stickyHeader aria-label={title} sx={{ minWidth: minTableWidth }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.key} sx={{ ...headCellSx, ...(column.numeric ? { textAlign: "right" } : {}) }}>
                      {column.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={rowKey(row, index)} hover>
                    {columns.map((column) => (
                      <TableCell key={column.key} sx={column.numeric ? numberSx : cellSx}>
                        {column.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
              {showTotals ? (
                <TableFooter>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{ ...(column.numeric ? numberSx : cellSx), ...text(13.5, NEUTRAL.text, 700), borderBottom: 0 }}
                      >
                        {column.total ?? null}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </Box>
        )}
        {footnote ? <Box sx={{ mt: 2 }}>{footnote}</Box> : null}
      </DialogContent>
    </Dialog>
  );
}

export default SourceListDialog;
